/**
 * AnalyzeContent — parent/orchestrator
 * Layout: 2-column (input | empty-state) before analysis,
 *         compact input bar + full-width results after analysis.
 */
import React, { useState, useCallback } from 'react';
import {
  Box, Grid, Typography, Paper, Button, Chip,
  CircularProgress, alpha, useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  Bolt as BoltIcon,
  TextFields as TextFieldsIcon,
} from '@mui/icons-material';
import InputPanel from './analyze/InputPanel';
import ResultsPanel from './analyze/ResultsPanel';
import PageAlert from './PageAlert';

// ── Local quick-prediction heuristic ────────────────────────────────────────
function computePrediction(content) {
  let score = 50;
  const suggestions = [];
  const wc = content.trim().split(/\s+/).length;

  if (wc >= 20 && wc <= 150) { score += 20; suggestions.push('Perfect length for engagement!'); }
  else if (wc < 20) { score -= 10; suggestions.push('Consider making your content longer.'); }
  else { score -= 5; suggestions.push('Long content may lose reader attention.'); }

  if (content.includes('?')) { score += 10; suggestions.push('Questions drive interaction!'); }
  if (/[\u{1F300}-\u{1FAFF}]/u.test(content)) { score += 10; suggestions.push('Emojis boost visual appeal!'); }
  if (/\d/.test(content)) { score += 10; suggestions.push('Numbers add credibility.'); }
  if (/comment|share|thoughts|follow/i.test(content)) { score += 15; suggestions.push('Great call-to-action!'); }

  score = Math.max(0, Math.min(100, score));
  const virality = score >= 80 ? 'High' : score >= 60 ? 'Medium' : 'Low';
  let best = 'Twitter';
  if (/professional|business|career/i.test(content)) best = 'LinkedIn';
  else if (/photo|image|video|visual/i.test(content)) best = 'Instagram';
  else if (wc > 100) best = 'LinkedIn';

  return { score: Math.round(score), virality, best, suggestions };
}

// ── Compact input summary bar ─────────────────────────────────────────────────
function CompactBar({ text, selectedPlatform, loading, onEdit, onReanalyse }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isUrl = /^https?:\/\//i.test(text.trim());
  const preview = text.length > 120 ? text.slice(0, 120) + '…' : text;

  return (
    <Paper elevation={0} sx={{
      p: 2, mb: 3, borderRadius: '12px',
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
    }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: '8px', flexShrink: 0,
        bgcolor: alpha('#6366f1', isDark ? 0.2 : 0.1),
        color: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <TextFieldsIcon sx={{ fontSize: 16 }} />
      </Box>

      <Typography variant="body2" sx={{
        flex: 1, color: 'text.secondary', fontSize: '0.82rem',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        minWidth: 0,
      }}>
        {preview || '—'}
      </Typography>

      {selectedPlatform && (
        <Chip
          label={selectedPlatform}
          size="small"
          sx={{ fontWeight: 700, fontSize: '0.72rem', borderRadius: '6px', height: 24 }}
        />
      )}

      <Button
        size="small" variant="outlined" startIcon={<EditIcon sx={{ fontSize: 14 }} />}
        onClick={onEdit}
        sx={{ borderRadius: '8px', fontSize: '0.78rem', fontWeight: 700, py: 0.5, px: 1.5, flexShrink: 0 }}
      >
        {isUrl ? 'Change URL' : 'Edit'}
      </Button>

      <Button
        size="small" variant="contained"
        startIcon={loading
          ? <CircularProgress size={12} color="inherit" />
          : <BoltIcon sx={{ fontSize: 14 }} />}
        onClick={onReanalyse} disabled={loading || !text.trim()}
        sx={{
          borderRadius: '8px', fontSize: '0.78rem', fontWeight: 700, py: 0.5, px: 1.5, flexShrink: 0,
          background: 'linear-gradient(135deg,#4f46e5,#7c3aed)',
          '&:disabled': { background: alpha('#6366f1', 0.3), color: 'rgba(255,255,255,0.5)' },
        }}
      >
        {loading ? 'Analysing…' : 'Re-analyse'}
      </Button>
    </Paper>
  );
}

// ════════════════════════════════════════════════════════════════════════════
const AnalyzeContent = React.memo(({ onAnalyze, loading, analysis, error }) => {
  const [text, setText]                   = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [prediction, setPrediction]       = useState(null);
  const [predLoading, setPredLoading]     = useState(false);
  const [editing, setEditing]             = useState(true); // show input panel

  // handleSubmit — used by InputPanel's Analyse button (collapses panel + triggers prediction)
  const handleSubmit = useCallback(async () => {
    if (!text.trim()) return;
    setEditing(false);
    onAnalyze(text, selectedPlatform);
    setPredLoading(true);
    await new Promise(r => setTimeout(r, 900));
    setPrediction(computePrediction(text));
    setPredLoading(false);
  }, [text, selectedPlatform, onAnalyze]);

  // handleReanalyse — used by CompactBar's Re-analyse button (stays in compact mode)
  const handleReanalyse = useCallback(() => {
    if (!text.trim()) return;
    onAnalyze(text, selectedPlatform);
  }, [text, selectedPlatform, onAnalyze]);

  const handleEdit = useCallback(() => {
    setEditing(true);
  }, []);

  // ══════════════════════════════════════════════════════════════════════════
  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', mb: 1 }}>
          Content IQ
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          AI-powered sentiment, engagement, and virality analysis for your content
        </Typography>
      </Box>

      <PageAlert error={error} />

      {/* ── EDITING MODE: 2-column ───────────────────────────────────────── */}
      {editing && (
        <Grid container spacing={4} alignItems="stretch">
          <Grid size={{ xs: 12, lg: 4 }}>
            <InputPanel
              text={text}
              onTextChange={setText}
              selectedPlatform={selectedPlatform}
              onPlatformChange={setSelectedPlatform}
              onSubmit={handleSubmit}
              loading={loading}
              predLoading={predLoading}
              prediction={prediction}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 8 }} sx={{ display: 'flex', flexDirection: 'column', minHeight: 600 }}>
            <ResultsPanel analysis={analysis} loading={loading} />
          </Grid>
        </Grid>
      )}

      {/* ── RESULTS MODE: compact bar + full-width results ───────────────── */}
      {!editing && (
        <Box>
          <CompactBar
            text={text}
            selectedPlatform={selectedPlatform}
            loading={loading}
            onEdit={handleEdit}
            onReanalyse={handleReanalyse}
          />
          <ResultsPanel analysis={analysis} loading={loading} fullWidth />
        </Box>
      )}
    </Box>
  );
});

export default AnalyzeContent;
